import { DateTime } from 'luxon';

import { AuditTrailItemType } from '@/common/enums/audit-trail-item-type.enum';
import Exception from '@/common/models/Exception';
import { fromServerUtcDate } from '@/globals';

import AuditItemType from './AuditItemType.enum';
import AuditTrailItem from './AuditTrailItem';
import AuditTrailItemChange from './AuditTrailItemChange';

const AuditTrailExclusions = ['LineItemTargetingCriterion']; // LineItemTargetingCriterion changes are handled using LineItemTargetingCriterionValue

interface IOperation {
  operation_order: number;
  operation_time_utc: string;
  operation_type: string;
  operation_user: string;
  operation_user_profile_picture_url: string;

  geo_json?: string;
  value?: never;
}

interface Trail {
  entity: string;
  operations: AuditTrailItem[];
}

export default class AuditTrail {
  trail: Trail[];

  serverTrail: Record<string, IOperation[]>;

  mainEntityName: string;

  operations: AuditTrailItem[];

  visibility: boolean;

  constructor(serverTrail: Record<string, IOperation[]>, mainEntityName: string) {
    this.trail = null;
    this.serverTrail = this.filterKnownAuditTypes(serverTrail);
    this.mainEntityName = mainEntityName;
    this.operations = [];
    this.processTrail();
    this.visibility = true;
  }

  filterKnownAuditTypes(serverTrail) {
    return Object.keys(serverTrail)
      .filter((key) => key in AuditItemType)
      .reduce(
        (obj, key) => {
          obj[key] = serverTrail[key];
          return obj;
        },
        {} as Record<AuditItemType, IOperation[]>,
      );
  }

  get orderedOperations() {
    const groups = this.operations.reduce((mem, op) => {
      const key = op.datetime.toLocaleString(DateTime.DATE_SHORT);
      const grouped = { ...mem };
      grouped[key] = [...(mem[key] != null ? mem[key] : []), op];
      return grouped;
    }, {});

    for (const key in groups) {
      groups[key].sort((a: AuditTrailItem, b: AuditTrailItem) => {
        // This is some weird sorting logic to ensure that the entity creation is always the first operation.
        // For some reason the server returns the creation operation after modification operations from time to time.
        if (a.action === 'Created') return -1;
        if (b.action === 'Created') return 1;
        return a.datetime.toSeconds() - b.datetime.toSeconds();
      });
    }

    // Resorted since the ordered operations display retirement after the modification has been made.
    const sortedKeys = Object.keys(groups).sort((a, b) => Date.parse(b) - Date.parse(a));
    const sortedOps = {};
    sortedKeys.forEach((key) => (sortedOps[key] = groups[key].reverse()));

    return sortedOps;
  }

  toggleItemsVisibility() {
    this.visibility = !this.visibility;
    this.operations.forEach((op: AuditTrailItem) => {
      op.isVisible = this.visibility;
    });
  }

  processTrail() {
    this.operations = [];
    this.trail = Object.keys(this.serverTrail)
      .filter((x: string) => !AuditTrailExclusions.includes(x))
      .map((entityName: string): Trail => {
        const operations: AuditTrailItem[] = this.serverTrail[entityName].map((op, i) => {
          const currentAuditTrailItem: AuditTrailItem = new AuditTrailItem({
            mainEntityName: this.mainEntityName,
            entityName,
            index: op.operation_order,
            time: fromServerUtcDate(op.operation_time_utc).toLocal(),
            user: op.operation_user,
            profilePictureUrl: op.operation_user_profile_picture_url,
            entity: op,
          });
          const previousVersion = i > 0 ? this.serverTrail[entityName][i - 1] : null;
          const currentVersion = op;
          switch (op.operation_type) {
            case 'CREATE': {
              currentAuditTrailItem.type = AuditTrailItemType.CREATE;
              switch (currentAuditTrailItem.entityName) {
                case 'UnitGeofence': {
                  const currentValue = currentVersion.geo_json;
                  const changes: AuditTrailItemChange[] = [];
                  changes.push(new AuditTrailItemChange(currentAuditTrailItem, 'geo_json', currentValue, null));
                  currentAuditTrailItem.changes = changes;
                  break;
                }
                case 'LineItemTargetingCriterionValue': {
                  const currentValue = currentVersion.value;
                  const changes: AuditTrailItemChange[] = [];
                  changes.push(new AuditTrailItemChange(currentAuditTrailItem, 'value', currentValue, null));
                  currentAuditTrailItem.changes = changes;
                  break;
                }
                default:
                  break;
              }
              break;
            }
            case 'UPDATE': {
              if (previousVersion === null) {
                currentAuditTrailItem.type = AuditTrailItemType.CREATE;
                break;
              }
              const changes: AuditTrailItemChange[] = [];
              const properties: string[] = Object.keys(currentVersion).filter(
                (p: string) =>
                  !p.startsWith('operation_') &&
                  p.localeCompare('modified_on_utc') !== 0 &&
                  p.localeCompare('pacing_reset_date') !== 0,
              );
              properties.forEach((p: string) => {
                const currentValue = currentVersion[p];
                const previousValue = previousVersion[p];
                if (currentValue instanceof Array) {
                  if (JSON.stringify(currentValue).localeCompare(JSON.stringify(previousValue)) !== 0) {
                    changes.push(new AuditTrailItemChange(currentAuditTrailItem, p, currentValue, previousValue));
                  }
                } else if (currentValue !== previousValue) {
                  if (
                    currentValue !== null &&
                    previousValue !== null &&
                    currentValue instanceof String &&
                    previousValue instanceof String
                  ) {
                    if (currentValue.localeCompare(`${previousValue}`) === 0) {
                      return;
                    }
                  }
                  changes.push(new AuditTrailItemChange(currentAuditTrailItem, p, currentValue, previousValue));
                }
              });
              currentAuditTrailItem.type = AuditTrailItemType.UPDATE;
              currentAuditTrailItem.changes = changes;
              break;
            }
            case 'DELETE': {
              currentAuditTrailItem.type = AuditTrailItemType.DELETE;
              break;
            }
            default:
              throw new Exception(`Unsupported operation type "${op.operation_type}".`);
          }

          if (
            currentAuditTrailItem.type !== AuditTrailItemType.UPDATE ||
            (currentAuditTrailItem.changes != null && currentAuditTrailItem.changes.length > 0)
          ) {
            this.operations.push(currentAuditTrailItem);
          }
          return currentAuditTrailItem;
        });

        return {
          entity: entityName,
          operations,
        };
      });
  }
}
